import { format } from 'date-fns';

export interface ActionDeadline {
  text: string;
  isOverdue: boolean;
  urgency: 'high' | 'medium' | 'low';
}

export function formatTimestamp(dateString: string, timezone: string = 'America/New_York'): string {
  try {
    const date = new Date(dateString);
    
    // Format in ET timezone by default (can be made configurable per user)
    return format(date, 'MMM d, h:mm a') + ' ET';
  } catch (error) {
    console.error('Error formatting timestamp:', error, dateString);
    return 'Invalid date';
  }
}

// Helper to get urgency from action data
export function getUrgencyFromAction(action: any): 'ASAP' | 'Today' | 'Tomorrow' | null {
  if (!action.action_data) return null;
  
  const urgency = action.action_data.urgency?.toLowerCase();
  const callbackPreference = action.action_data.callback_preference?.toLowerCase();
  
  // Check various urgency indicators
  if (urgency === 'asap' || urgency === 'urgent' || callbackPreference === 'asap') {
    return 'ASAP';
  }
  if (urgency === 'today' || callbackPreference === 'today') {
    return 'Today';
  }
  if (urgency === 'tomorrow' || callbackPreference === 'tomorrow') {
    return 'Tomorrow';
  }
  
  return null;
}

// Get suggested due date based on urgency
export function getSuggestedDueDate(createdAt: string, urgency?: 'ASAP' | 'Today' | 'Tomorrow' | null): Date {
  const createdDate = new Date(createdAt);
  const now = new Date();
  
  switch (urgency) {
    case 'ASAP':
      // 90 minutes from call time
      return new Date(createdDate.getTime() + 90 * 60 * 1000);
    
    case 'Today':
      // End of business day (5 PM ET)
      const eod = new Date(createdDate);
      eod.setHours(17, 0, 0, 0); // 5 PM
      // If it's already past 5 PM or the EOD is in the past, use tomorrow 9 AM
      if (eod <= now) {
        const tomorrow9am = new Date(createdDate);
        tomorrow9am.setDate(tomorrow9am.getDate() + 1);
        tomorrow9am.setHours(9, 0, 0, 0);
        return tomorrow9am;
      }
      return eod;
    
    case 'Tomorrow':
      // Next business day 9 AM
      const tomorrow = new Date(createdDate);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      // If tomorrow is weekend, move to Monday
      const dayOfWeek = tomorrow.getDay();
      if (dayOfWeek === 0) { // Sunday -> Monday
        tomorrow.setDate(tomorrow.getDate() + 1);
      } else if (dayOfWeek === 6) { // Saturday -> Monday
        tomorrow.setDate(tomorrow.getDate() + 2);
      }
      return tomorrow;
    
    default:
      // No urgency: +24 hours from call
      return new Date(createdDate.getTime() + 24 * 60 * 60 * 1000);
  }
}

export function getActionDeadline(actionType: string, createdAt: string, callMode: string = 'outbound'): ActionDeadline {
  try {
    const createdDate = new Date(createdAt);
    const now = new Date();
    
    let hoursToDeadline = 24; // Default 24 hours
    let urgencyThresholdHours = 6; // When it becomes high priority
    
    const isInbound = callMode === 'inbound';
    
    // Adjust deadline based on action type and call direction
    switch (actionType) {
      case 'follow_up_call':
      case 'consent_callback':
        // Inbound: 2-4 hours (prospects called with immediate needs)
        // Outbound: 24-48 hours (framed as 'later this week')
        hoursToDeadline = isInbound ? 3 : 36; 
        urgencyThresholdHours = isInbound ? 1 : 12;
        break;
      case 'appointment_interest':
      case 'appointment_booked':
        // Appointments are always urgent regardless of direction
        hoursToDeadline = 2; 
        urgencyThresholdHours = 0.5;
        break;
      case 'email_provided':
      case 'email_promise':
        // Inbound: 4 hours (immediate interest)
        // Outbound: 24 hours (standard follow-up)
        hoursToDeadline = isInbound ? 4 : 24;
        urgencyThresholdHours = isInbound ? 1 : 4;
        break;
      case 'message_taking':
        // Messages should be passed quickly regardless of direction
        hoursToDeadline = 2;
        urgencyThresholdHours = 1;
        break;
    }
    
    const deadlineDate = new Date(createdDate.getTime() + hoursToDeadline * 60 * 60 * 1000);
    const hoursUntilDeadline = (deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    const isOverdue = hoursUntilDeadline < 0;
    const urgency: 'high' | 'medium' | 'low' = 
      isOverdue || hoursUntilDeadline < urgencyThresholdHours ? 'high' :
      hoursUntilDeadline < hoursToDeadline / 2 ? 'medium' : 'low';
    
    let text = '';
    if (isOverdue) {
      const hoursOverdue = Math.abs(hoursUntilDeadline);
      if (hoursOverdue < 1) {
        text = `Overdue by ${Math.round(hoursOverdue * 60)}m`;
      } else {
        text = `Overdue by ${Math.round(hoursOverdue)}h`;
      }
    } else {
      // Calculate target day and time
      const dayOfWeek = format(deadlineDate, 'EEE'); // Mon, Tue, etc
      const time = format(deadlineDate, 'h:mm a'); // 3:00 PM
      
      if (hoursUntilDeadline < 24) {
        text = `Due: ${dayOfWeek} ${time} ET`;
      } else {
        text = `Due: ${format(deadlineDate, 'MMM d')} ${time} ET`;
      }
    }
    
    return { text, isOverdue, urgency };
  } catch (error) {
    console.error('Error calculating deadline:', error, actionType, createdAt);
    return { text: 'Unable to calculate deadline', isOverdue: false, urgency: 'low' };
  }
}

export function getManualDeadline(dueAt: string): ActionDeadline {
  try {
    const dueDate = new Date(dueAt);
    const now = new Date();
    
    const hoursUntilDeadline = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    const isOverdue = hoursUntilDeadline < 0;
    
    // For manually set deadlines, urgency is based on time remaining
    const urgency: 'high' | 'medium' | 'low' = 
      isOverdue || hoursUntilDeadline < 2 ? 'high' :
      hoursUntilDeadline < 12 ? 'medium' : 'low';
    
    let text = '';
    if (isOverdue) {
      const hoursOverdue = Math.abs(hoursUntilDeadline);
      if (hoursOverdue < 1) {
        text = `Overdue by ${Math.round(hoursOverdue * 60)}m`;
      } else {
        text = `Overdue by ${Math.round(hoursOverdue)}h`;
      }
    } else {
      // Calculate target day and time
      const dayOfWeek = format(dueDate, 'EEE'); // Mon, Tue, etc
      const time = format(dueDate, 'h:mm a'); // 3:00 PM
      
      if (hoursUntilDeadline < 24) {
        text = `Due: ${dayOfWeek} ${time} ET`;
      } else {
        text = `Due: ${format(dueDate, 'MMM d')} ${time} ET`;
      }
    }
    
    return { text, isOverdue, urgency };
  } catch (error) {
    console.error('Error calculating manual deadline:', error, dueAt);
    return { text: 'Unable to calculate deadline', isOverdue: false, urgency: 'low' };
  }
}

export function getActionDeadlineDate(actionType: string, createdAt: string, callMode: string = 'outbound'): Date {
  try {
    const createdDate = new Date(createdAt);
    let hoursToDeadline = 24; // Default 24 hours
    
    const isInbound = callMode === 'inbound';
    
    // Use same logic as getActionDeadline for consistency
    switch (actionType) {
      case 'follow_up_call':
      case 'consent_callback':
        hoursToDeadline = isInbound ? 3 : 36;
        break;
      case 'appointment_interest':
      case 'appointment_booked':
        hoursToDeadline = 2;
        break;
      case 'email_provided':
      case 'email_promise':
        hoursToDeadline = isInbound ? 4 : 24;
        break;
      case 'message_taking':
        hoursToDeadline = 2;
        break;
    }
    
    return new Date(createdDate.getTime() + hoursToDeadline * 60 * 60 * 1000);
  } catch (error) {
    console.error('Error calculating deadline date:', error, actionType, createdAt);
    return new Date(); // Return current date as fallback
  }
}